import { MAX_PLAYERS, MISSES_PHRASE, MISSES_WORD, SOLVE_BONUS, TURN_MS, type Category } from "../src/protocol";
import type { Lang } from "../src/languages";
import { LANG } from "./i18n";

// Every visible text lives here, once per language. The `Strings` type is derived from the
// English table, so a key missing in Spanish is a compile error.
//
// Wording: WorkBrawl avoids "game"/"play" vocabulary (some workplace filters block it).
// Say round, room, start, people, title, puzzle.

const TURN_SECONDS = TURN_MS / 1000;

const en = {
  appName: "StickBrawl",
  pageTitle: "StickBrawl — Team hangman against the clock",
  tagline: "Team hangman against the clock",
  homeIntro: "Open a room, share the link and take turns guessing letters. Solve as many puzzles as you can before time runs out.",
  uiLanguageLabel: "Language",
  backToHub: "Back to WorkBrawl",
  privacy: "Privacy",
  terms: "Terms",
  consentText: "We use analytics cookies to learn how WorkBrawl is used. They\'re off unless you accept.",
  consentAccept: "Accept",
  consentReject: "Reject",
  consentNotice: "We use Google Analytics cookies to understand how WorkBrawl is used and improve it.",
  consentGotIt: "Got it",

  newRoomTitle: "New room",
  nameLabel: "Your name",
  namePlaceholder: "e.g. Alex",
  nameInvalid: "Enter a name of 1 to 20 characters.",
  durationLabel: "Round length",
  minutes: (n: number) => `${n} minutes`,
  puzzleLangLabel: "Puzzle language",
  categoryLabel: "Category",
  categories: {
    mixed: "A bit of everything",
    sayings: "Sayings",
    everyday: "Everyday expressions",
    food: "Food and drink",
    places: "Places",
    office: "Office life",
    nature: "Nature",
  } satisfies Record<Category | "mixed", string>,
  createRoom: "Create room",
  creatingRoom: "Creating…",
  verifying: "Checking…",
  or: "or",
  codeLabel: "Room code",
  codePlaceholder: "ABCDE",
  codeInvalid: "Room codes have 5 letters or numbers.",
  join: "Join",
  createFailed: "Could not create the room. Try again.",
  joinFailed: "Could not check the room. Try again.",
  turnstileFailed: "The security check failed. Try again.",
  turnstileUnavailable: "The security check is unavailable. Reload the page and try again.",
  turnstileInteract: "Tick the box below to confirm you're a person.",
  errorRoomNotFound: "This room does not exist or has expired.",
  errorRoomFull: `This room is full (${MAX_PLAYERS} people).`,
  invalidRoomLink: "That room link isn't valid.",

  howTitle: "How it works",
  howSteps: [
    { title: "Open a room.", text: "Pick the round length, the language and a category, then share the link or the QR code." },
    { title: "Take turns.", text: `Each person gives one letter, then the turn moves on. You have ${TURN_SECONDS} seconds to choose.` },
    { title: "Save the stick figure.", text: `Words allow ${MISSES_WORD} wrong letters and phrases ${MISSES_PHRASE}. Solve as many as you can as a team before time is up.` },
  ],
  faqTitle: "Frequently asked questions",
  faq: [
    { q: "Is StickBrawl free?", a: "Yes. It's free, needs no account or download and runs in any modern browser." },
    { q: "How many people fit in a room?", a: `Up to ${MAX_PLAYERS} people per room, each on their own device.` },
    { q: "Do we compete against each other?", a: "No. The whole room is one team against the clock. The scoreboard shows what each person added, just for fun." },
    { q: "How are points counted?", a: `1 point each time your letter appears, plus ${SOLVE_BONUS} for the letter that completes the puzzle. Everything adds up to the team score.` },
    { q: "What if someone doesn't answer?", a: `After ${TURN_SECONDS} seconds the turn moves to the next person, with no penalty.` },
  ],

  namePromptTitle: "What's your name?",
  namePromptBody: "This is how the others will see you in the room.",
  enterRoom: "Enter room",

  room: "Room",
  exit: "Leave",
  cancel: "Cancel",
  exitTitle: "Leave the room?",
  exitBody: "You'll go back to the start screen. Come back with the room link to rejoin.",
  team: "Team",
  teamScore: "Team score",
  solvedCount: (n: number) => (n === 1 ? "1 solved" : `${n} solved`),
  lostCount: (n: number) => (n === 1 ? "1 lost" : `${n} lost`),
  contributions: "Who added what",
  timeLeft: "Time left",
  you: "you",
  offline: "away",
  points: (n: number) => (n === 1 ? "1 point" : `${n} points`),

  inviteTitle: "Invite your team",
  copyLink: "Copy link",
  linkCopied: "Copied!",
  linkCopiedToast: "Link copied",
  copyFailed: "Copy this link:",
  copyFailedShort: "Couldn't copy",
  share: "Share",
  shareText: "Join me on StickBrawl: team hangman against the clock!",
  close: "Close",
  qrLabel: (code: string) => `QR code to join room ${code}`,

  lobbyTitle: "Ready when you are",
  lobbyBody: (n: number) => (n === 1 ? "You're the only one here. Share the link, or start on your own." : `${n} people in the room.`),
  startRound: "Start round",
  getReady: "Get ready",
  puzzleNumber: (n: number) => `Puzzle ${n}`,
  yourTurn: "Your turn: pick a letter",
  turnOf: (name: string) => `${name}'s turn`,
  waitingTurn: "Waiting for the next turn…",
  missesLeft: (n: number) => (n === 1 ? "1 miss left" : `${n} misses left`),
  solvedBanner: "Solved!",
  lostBanner: "Out of tries",
  keyboardLabel: "Letters",
  figureLabel: (misses: number, max: number) => `Stick figure: ${misses} of ${max} wrong letters`,
  resultsTitle: "Time's up!",
  resultsScore: (n: number) => (n === 1 ? "Your team scored 1 point" : `Your team scored ${n} points`),
  unfinished: (answer: string) => `The last one was: ${answer}`,
  newRound: "New round",

  connecting: "Connecting…",
  reconnecting: "Connection lost. Reconnecting…",
  backHome: "Back to start",
};

export type Strings = typeof en;

const es: Strings = {
  appName: "StickBrawl",
  pageTitle: "StickBrawl — El ahorcado en equipo contra el reloj",
  tagline: "El ahorcado en equipo contra el reloj",
  homeIntro: "Abre una sala, comparte el enlace y adivinen letras por turnos. Resuelvan todos los acertijos que puedan antes de que se acabe el tiempo.",
  uiLanguageLabel: "Idioma",
  backToHub: "Volver a WorkBrawl",
  privacy: "Privacidad",
  terms: "Términos",
  consentText: "Usamos cookies de analítica para saber cómo se usa WorkBrawl. Están desactivadas salvo que las aceptes.",
  consentAccept: "Aceptar",
  consentReject: "Rechazar",
  consentNotice: "Usamos cookies de Google Analytics para entender cómo se usa WorkBrawl y mejorarlo.",
  consentGotIt: "Entendido",

  newRoomTitle: "Nueva sala",
  nameLabel: "Tu nombre",
  namePlaceholder: "p. ej. Álex",
  nameInvalid: "Escribe un nombre de 1 a 20 caracteres.",
  durationLabel: "Duración de la ronda",
  minutes: (n: number) => `${n} minutos`,
  puzzleLangLabel: "Idioma de los acertijos",
  categoryLabel: "Categoría",
  categories: {
    mixed: "De todo un poco",
    sayings: "Refranes",
    everyday: "Expresiones de todos los días",
    food: "Comida y bebida",
    places: "Lugares",
    office: "Vida de oficina",
    nature: "Naturaleza",
  },
  createRoom: "Crear sala",
  creatingRoom: "Creando…",
  verifying: "Verificando…",
  or: "o",
  codeLabel: "Código de sala",
  codePlaceholder: "ABCDE",
  codeInvalid: "Los códigos de sala tienen 5 letras o números.",
  join: "Entrar",
  createFailed: "No se pudo crear la sala. Inténtalo de nuevo.",
  joinFailed: "No se pudo comprobar la sala. Inténtalo de nuevo.",
  turnstileFailed: "La verificación de seguridad falló. Inténtalo de nuevo.",
  turnstileUnavailable: "La verificación de seguridad no está disponible. Recarga la página e inténtalo de nuevo.",
  turnstileInteract: "Marca la casilla de abajo para confirmar que eres una persona.",
  errorRoomNotFound: "Esta sala no existe o ya expiró.",
  errorRoomFull: `Esta sala está llena (${MAX_PLAYERS} personas).`,
  invalidRoomLink: "El enlace de la sala no es válido.",

  howTitle: "Cómo funciona",
  howSteps: [
    { title: "Abre una sala.", text: "Elige la duración de la ronda, el idioma y una categoría, y comparte el enlace o el código QR." },
    { title: "Por turnos.", text: `Cada persona da una letra y el turno pasa a la siguiente. Tienes ${TURN_SECONDS} segundos para elegir.` },
    { title: "Salven al muñeco.", text: `Las palabras permiten ${MISSES_WORD} letras equivocadas y las frases ${MISSES_PHRASE}. Resuelvan en equipo todas las que puedan antes de que acabe el tiempo.` },
  ],
  faqTitle: "Preguntas frecuentes",
  faq: [
    { q: "¿StickBrawl es gratis?", a: "Sí. Es gratis, no necesita cuenta ni descarga y funciona en cualquier navegador moderno." },
    { q: "¿Cuántas personas caben en una sala?", a: `Hasta ${MAX_PLAYERS} personas por sala, cada una en su propio dispositivo.` },
    { q: "¿Competimos entre nosotros?", a: "No. Toda la sala es un solo equipo contra el reloj. El marcador muestra lo que sumó cada persona, solo por diversión." },
    { q: "¿Cómo se cuentan los puntos?", a: `1 punto por cada vez que aparece tu letra, más ${SOLVE_BONUS} por la letra que completa el acertijo. Todo se suma al puntaje del equipo.` },
    { q: "¿Y si alguien no responde?", a: `Después de ${TURN_SECONDS} segundos el turno pasa a la siguiente persona, sin penalización.` },
  ],

  namePromptTitle: "¿Cómo te llamas?",
  namePromptBody: "Así te verán los demás en la sala.",
  enterRoom: "Entrar a la sala",

  room: "Sala",
  exit: "Salir",
  cancel: "Cancelar",
  exitTitle: "¿Salir de la sala?",
  exitBody: "Volverás al inicio. Puedes regresar con el enlace de la sala.",
  team: "Equipo",
  teamScore: "Puntaje del equipo",
  solvedCount: (n: number) => (n === 1 ? "1 resuelto" : `${n} resueltos`),
  lostCount: (n: number) => (n === 1 ? "1 perdido" : `${n} perdidos`),
  contributions: "Quién sumó qué",
  timeLeft: "Tiempo restante",
  you: "tú",
  offline: "ausente",
  points: (n: number) => (n === 1 ? "1 punto" : `${n} puntos`),

  inviteTitle: "Invita a tu equipo",
  copyLink: "Copiar enlace",
  linkCopied: "¡Copiado!",
  linkCopiedToast: "Enlace copiado",
  copyFailed: "Copia este enlace:",
  copyFailedShort: "No se pudo copiar",
  share: "Compartir",
  shareText: "¡Únete a StickBrawl: el ahorcado en equipo contra el reloj!",
  close: "Cerrar",
  qrLabel: (code: string) => `Código QR para entrar a la sala ${code}`,

  lobbyTitle: "Cuando quieran",
  lobbyBody: (n: number) => (n === 1 ? "Por ahora solo estás tú. Comparte el enlace, o empieza por tu cuenta." : `${n} personas en la sala.`),
  startRound: "Empezar ronda",
  getReady: "Prepárate",
  puzzleNumber: (n: number) => `Acertijo ${n}`,
  yourTurn: "Tu turno: elige una letra",
  turnOf: (name: string) => `Turno de ${name}`,
  waitingTurn: "Esperando el siguiente turno…",
  missesLeft: (n: number) => (n === 1 ? "Queda 1 fallo" : `Quedan ${n} fallos`),
  solvedBanner: "¡Resuelto!",
  lostBanner: "Se acabaron los intentos",
  keyboardLabel: "Letras",
  figureLabel: (misses: number, max: number) => `Muñeco: ${misses} de ${max} letras equivocadas`,
  resultsTitle: "¡Se acabó el tiempo!",
  resultsScore: (n: number) => (n === 1 ? "Su equipo sumó 1 punto" : `Su equipo sumó ${n} puntos`),
  unfinished: (answer: string) => `El último era: ${answer}`,
  newRound: "Nueva ronda",

  connecting: "Conectando…",
  reconnecting: "Se perdió la conexión. Reconectando…",
  backHome: "Volver al inicio",
};

const TABLES: Record<Lang, Strings> = { en, es };

export const STRINGS: Strings = TABLES[LANG];

/** Each language written in itself, for the language switcher. */
export const LANGUAGE_NAMES: Record<Lang, string> = { en: "English", es: "Español" };
